
// ============================================================================
// physics.js — 2D pinball physics: vector helpers, collider/sensor factories,
// Flipper, Ball and the World stepper (continuous collision detection with a
// discrete push-out pass, a rolling-resistance friction model, a flipper
// contact model, ball-ball collisions, swept line sensors, a uniform-grid
// broadphase and per (collider, ball) event throttling).
//
// Units: metres, seconds, radians. The playfield is a top-down 2D plane; the
// table's 6.5 degree incline is folded into World.gravity. The stepper is
// deterministic: it never calls Math.random and every traversal runs in
// collider-index / ball-index order, so ties resolve the same way on every run.
//
// Public names: DT, SUBSTEPS, SUBSTEP_DT, REST_SPEED, CREEP_SPEED, EPS, vec, dot, len, sub,
// add, scale, norm, perp, clamp, makeCollider, segment, circle, sensorCircle,
// sensorLine, Flipper, Ball, World, closestPointOnSegment, angleInArc,
// sweepCircle, sweepSegment, sweepArc, pushOut, sideOfLine, crossesSegment,
// restitutionScale, colliderBounds.
// Internal top-level helpers (not API): nextColliderId, nextBallId,
// colliderSignature, signatureChanged, nextQueryStamp.
// ============================================================================

/** Fixed simulation frame (120 Hz). Each frame is integrated in SUBSTEPS substeps. */
const DT = 1 / 120;
/** Substeps per frame and the substep length (480 Hz). */
const SUBSTEPS = 4;
const SUBSTEP_DT = DT / SUBSTEPS;
/**
 * Normal-speed boundary between an impact (Coulomb friction, restitution) and a
 * resting/rolling contact (rolling resistance only). See World.resolve.
 */
const REST_SPEED = 0.25;
/**
 * Tangential speed below which rolling resistance fades out linearly. Without
 * the fade a constant resistance would hold a ball balanced within
 * atan(rollingMu) of the apex of any post or cap; with it the only rest state
 * is a true equilibrium (no tangential load) and shallow slopes are crept
 * down at a few mm/s.
 */
const CREEP_SPEED = 0.004;
/** Generic tiny epsilon for divisions and "remaining time" checks. */
const EPS = 1e-9;

// ---------------------------------------------------------------------------
// Vector helpers (plain {x, y} objects; every op returns a fresh object)
// ---------------------------------------------------------------------------
const vec = (x = 0, y = 0) => ({ x, y });
const dot = (a, b) => a.x * b.x + a.y * b.y;
const len = (v) => Math.hypot(v.x, v.y);
const sub = (a, b) => vec(a.x - b.x, a.y - b.y);
const add = (a, b) => vec(a.x + b.x, a.y + b.y);
const scale = (v, s) => vec(v.x * s, v.y * s);
/** Unit vector; a zero vector normalises to itself (divides by 1). */
const norm = (v) => {
  const length = len(v) || 1;
  return vec(v.x / length, v.y / length);
};
/** 90 degree counter-clockwise rotation. */
const perp = (v) => vec(-v.y, v.x);
const clamp = (value, lo, hi) => Math.min(hi, Math.max(lo, value));

/**
 * Restitution ramp: 0 below 0.15 m/s of closing speed, the full coefficient
 * from 0.6 m/s, linear in between. Slow contacts settle without a hard cutoff.
 */
const restitutionScale = (closingSpeed) => clamp((closingSpeed - 0.15) / 0.45, 0, 1);

// ---------------------------------------------------------------------------
// Collider / sensor factories
// ---------------------------------------------------------------------------
let nextColliderId = 1;

/**
 * Base collider: restitution e, friction mu, layer bitmask, optional tag
 * (event name suffix), oneWay normal (ball passes when moving along it) and a
 * kind used by the renderer/rules ('wall' emits generic 'contact' events).
 */
function makeCollider(props) {
  return Object.assign(
    { id: nextColliderId++, e: 0.12, mu: 0.008, layers: 1, tag: null, active: true, oneWay: null, kind: 'wall' },
    props,
  );
}

/** Capsule wall from (x0,y0) to (x1,y1) with half-thickness r. */
const segment = (x0, y0, x1, y1, r = 0.004, props = {}) =>
  makeCollider({ type: 'seg', a: vec(x0, y0), b: vec(x1, y1), r, ...props });

/** Round post / pop bumper body. */
const circle = (x, y, r, props = {}) => makeCollider({ type: 'circle', c: vec(x, y), r, ...props });

/** Circular trigger area; tracks which ball ids are inside for enter/exit events. */
const sensorCircle = (x, y, r, tag, props = {}) => ({
  type: 'scircle',
  c: vec(x, y),
  r,
  tag,
  layers: 1,
  active: true,
  inside: new Set(),
  ...props,
});

/** Line trigger; emits 'cross:<tag>' when a ball's frame path crosses the segment. */
const sensorLine = (x0, y0, x1, y1, tag, props = {}) => ({
  type: 'sline',
  a: vec(x0, y0),
  b: vec(x1, y1),
  tag,
  layers: 1,
  active: true,
  ...props,
});

// ---------------------------------------------------------------------------
// Flipper
// ---------------------------------------------------------------------------
class Flipper {
  /**
   * A flipper is a capsule from pivot to tip (radius r0 at the pivot, r1 at
   * the tip) rotating between restAngle and endAngle. omega/retOmega are the
   * target angular speeds (rad/s) when pressed/releasing; servoUp/servoDown
   * are per-frame blend factors toward that target (a first-order servo).
   */
  constructor({
    pivot,
    length,
    restAngle,
    endAngle,
    r0 = 0.01105,
    r1 = 0.01105,
    omega = 55,
    retOmega = 50,
    servoUp = 0.86,
    servoDown = 0.715,
    side = 'L',
  }) {
    this.pivot = pivot;
    this.length = length;
    this.rest = restAngle;
    this.end = endAngle;
    this.r0 = r0;
    this.r1 = r1;
    this.omega = omega;
    this.retOmega = retOmega;
    this.servoUp = servoUp;
    this.servoDown = servoDown;
    this.side = side;
    this.angle = restAngle;
    this.prevAngle = restAngle;
    this.angVel = 0;
    // Angular velocity the contact model used in the current substep: equal to
    // angVel, i.e. 0 while the bat is clamped at a stop. Kept for tooling.
    this._frameVel = 0;
    this.pressed = false;
    this.enabled = true;
    // +1 when the stroke increases the angle (left flipper), -1 otherwise.
    this.dir = Math.sign(endAngle - restAngle);
    this._lo = Math.min(restAngle, endAngle);
    this._hi = Math.max(restAngle, endAngle);
  }

  /** Tip position for a given angle (defaults to the current one). */
  tip(angle = this.angle) {
    return vec(this.pivot.x + Math.cos(angle) * this.length, this.pivot.y + Math.sin(angle) * this.length);
  }

  /** Capsule radius at parameter t in [0,1] along pivot->tip. */
  radiusAt(t) {
    return this.r0 + (this.r1 - this.r0) * t;
  }

  /**
   * Advance the servo by dt: blend angVel toward the target, clamp at the
   * stops (angVel becomes 0 there, so a bat resting on a stop has no surface
   * velocity).
   */
  substep(dt) {
    this.prevAngle = this.angle;
    const driven = this.pressed && this.enabled;
    const targetVel = driven ? this.omega * this.dir : -this.retOmega * this.dir;
    const servo = driven ? this.servoUp : this.servoDown;
    // Servo factor is specified per 120 Hz frame; rescale it to this substep.
    const blend = 1 - Math.pow(1 - servo, dt * 120);
    this.angVel += (targetVel - this.angVel) * blend;
    let angle = this.angle + this.angVel * dt;
    if (angle <= this._lo) {
      angle = this._lo;
      this.angVel = 0;
    } else if (angle >= this._hi) {
      angle = this._hi;
      this.angVel = 0;
    }
    this.angle = angle;
    this._frameVel = this.angVel;
  }
}

// ---------------------------------------------------------------------------
// Ball
// ---------------------------------------------------------------------------
let nextBallId = 1;

class Ball {
  /**
   * p/v are position/velocity, prevP the position at the start of the current
   * substep, layer the collision layer bitmask (ramps live on other layers),
   * z/prevZ the visual height, held = parked in a scoop/plunger (not
   * simulated), hidden = held out of sight so the renderer skips it (rules.js
   * sets it while a ball sits in the scoop or a lock),
   * rampId/rampS = ramp travel state, spin = accumulated roll.
   * Runtime-only fields set by World: prevOuter (frame start position),
   * _side<L|R|U> and _flipVel<L|R|U> (flipper contact bookkeeping), _lastEv
   * (Map collider -> time of the last event emitted for this ball on it).
   */
  constructor(x, y, r = 0.0135) {
    this.id = nextBallId++;
    this.p = vec(x, y);
    this.v = vec(0, 0);
    this.prevP = vec(x, y);
    this.r = r;
    this.layer = 1;
    this.z = 0;
    this.prevZ = 0;
    this.held = false;
    this.hidden = false;
    this.active = true;
    this.rampId = null;
    this.rampS = 0;
    this.spin = 0;
  }
}

// ---------------------------------------------------------------------------
// Geometry / sweep helpers
// ---------------------------------------------------------------------------

/** Closest point q on segment a-b to p, plus its parameter t in [0,1]. */
function closestPointOnSegment(p, a, b) {
  const ab = sub(b, a);
  const t = clamp(dot(sub(p, a), ab) / (dot(ab, ab) + EPS), 0, 1);
  return { q: add(a, scale(ab, t)), t };
}

/** True when angle lies on the counter-clockwise arc from a0 to a1 (1e-6 slack). */
function angleInArc(angle, a0, a1) {
  const TWO_PI = Math.PI * 2;
  let span = (a1 - a0) % TWO_PI;
  if (span < 0) span += TWO_PI;
  let rel = (angle - a0) % TWO_PI;
  if (rel < 0) rel += TWO_PI;
  return rel <= span + 1e-6;
}

/**
 * Sweep a point p moving with velocity v for dt against a circle (centre c,
 * combined radius R). Returns {t, n} (time of impact, contact normal) or null.
 * Already-overlapping points hit at t=0 when moving inward.
 */
function sweepCircle(p, v, dt, c, R) {
  const rel = sub(p, c);
  const b = dot(rel, v);
  const cc = dot(rel, rel) - R * R;
  if (cc < 0) {
    const n = norm(rel);
    return dot(v, n) < 0 ? { t: 0, n } : null;
  }
  const a = dot(v, v);
  if (a < EPS) return null;
  const disc = b * b - a * cc;
  if (disc < 0) return null;
  const t = (-b - Math.sqrt(disc)) / a;
  if (t < 0 || t > dt) return null;
  const hit = add(p, scale(v, t));
  return { t, n: norm(sub(hit, c)) };
}

/**
 * Sweep a point against a capsule segment (combined radius R): test both
 * offset faces, then the two end caps; keep the earliest approaching hit.
 */
function sweepSegment(p, v, dt, seg, R) {
  const a = seg.a;
  const b = seg.b;
  const ab = sub(b, a);
  const length = len(ab);
  const dir = scale(ab, 1 / (length + EPS));
  const normal = perp(dir);
  const rel = sub(p, a);
  const distN = dot(rel, normal);
  const velN = dot(v, normal);
  let best = null;
  // Motion parallel to the segment cannot hit a face; only the caps can catch it.
  if (Math.abs(velN) > EPS) {
    for (const sign of [1, -1]) {
      const t = (sign * R - distN) / velN;
      if (t >= 0 && t <= dt) {
        const along = dot(rel, dir) + dot(v, dir) * t;
        if (along >= 0 && along <= length) {
          const n = scale(normal, sign);
          if (dot(v, n) < 0 && (!best || t < best.t)) best = { t, n };
        }
      }
    }
  }
  for (const endPoint of [a, b]) {
    const hit = sweepCircle(p, v, dt, endPoint, R);
    if (hit && (!best || hit.t < best.t)) best = hit;
  }
  return best;
}

/**
 * Sweep a ball (radius ballR) against an arc wall {c, R, r, a0, a1}: test the
 * outer and inner offset circles (both roots each, restricted to the arc's
 * angular span, normal flipped for the inner face), then the two end caps.
 * No table builds arcs today; the support is kept because it is correct.
 */
function sweepArc(p, v, dt, arc, ballR) {
  const { c, R: radius, a0, a1 } = arc;
  let best = null;
  for (const side of [1, -1]) {
    const offsetRadius = radius + side * (arc.r + ballR);
    if (offsetRadius <= 0) continue;
    const rel = sub(p, c);
    const a = dot(v, v);
    const b = dot(rel, v);
    const cc = dot(rel, rel) - offsetRadius * offsetRadius;
    if (a < EPS) continue;
    const disc = b * b - a * cc;
    if (disc < 0) continue;
    const root = Math.sqrt(disc);
    for (const t of [(-b - root) / a, (-b + root) / a]) {
      if (t < 0 || t > dt) continue;
      const hit = add(p, scale(v, t));
      const hitAngle = Math.atan2(hit.y - c.y, hit.x - c.x);
      if (!angleInArc(hitAngle, a0, a1)) continue;
      let n = norm(sub(hit, c));
      if (side < 0) n = scale(n, -1);
      if (!(dot(v, n) >= 0) && (!best || t < best.t)) best = { t, n };
    }
  }
  for (const endAngle of [arc.a0, arc.a1]) {
    const endPoint = vec(c.x + Math.cos(endAngle) * radius, c.y + Math.sin(endAngle) * radius);
    const hit = sweepCircle(p, v, dt, endPoint, arc.r + ballR);
    if (hit && (!best || hit.t < best.t)) best = hit;
  }
  return best;
}

/**
 * Discrete penetration fix: if the ball overlaps the collider (beyond a 1e-6
 * tolerance) move it out along the contact normal with a 1e-5 skin and return
 * that normal; otherwise return null. One-way segments never push from behind.
 */
function pushOut(ball, col) {
  const ballR = ball.r;
  if (col.type === 'seg') {
    const { q } = closestPointOnSegment(ball.p, col.a, col.b);
    const rel = sub(ball.p, q);
    const dist = len(rel);
    const minDist = col.r + ballR;
    if (dist < minDist - 1e-6) {
      // Exactly on the centre line: fall back to the segment's left normal.
      const n = dist > EPS ? scale(rel, 1 / dist) : perp(norm(sub(col.b, col.a)));
      if (col.oneWay && dot(n, col.oneWay) < 0) return null;
      ball.p = add(q, scale(n, minDist + 1e-5));
      return n;
    }
  } else if (col.type === 'circle') {
    const rel = sub(ball.p, col.c);
    const dist = len(rel);
    const minDist = col.r + ballR;
    if (dist < minDist - 1e-6) {
      const n = dist > EPS ? scale(rel, 1 / dist) : vec(0, 1);
      ball.p = add(col.c, scale(n, minDist + 1e-5));
      return n;
    }
  } else if (col.type === 'arc') {
    const rel = sub(ball.p, col.c);
    const dist = len(rel);
    const angle = Math.atan2(rel.y, rel.x);
    if (!angleInArc(angle, col.a0, col.a1)) return null;
    const minDist = col.r + ballR;
    const offset = dist - col.R;
    if (Math.abs(offset) < minDist - 1e-6) {
      const side = offset >= 0 ? 1 : -1;
      const n = scale(norm(rel), side);
      ball.p = add(col.c, scale(norm(rel), col.R + side * (minDist + 1e-5)));
      return n;
    }
  }
  return null;
}

/** Signed area test: >0 when p is left of the directed line a->b. */
function sideOfLine(p, a, b) {
  return (b.x - a.x) * (p.y - a.y) - (b.y - a.y) * (p.x - a.x);
}

/**
 * Crossing test between the path p0->p1 and the segment a-b extended by pad
 * beyond both ends. Returns the sign of the side p1 ends on (+1 left of a->b,
 * -1 right) when the path crosses the extended segment, else 0. Points exactly
 * on the line count as the right side, so a path that ends on the line is
 * reported once and not again when the next path starts there.
 */
function crossesSegment(p0, p1, a, b, pad) {
  const s0 = sideOfLine(p0, a, b);
  const s1 = sideOfLine(p1, a, b);
  const side0 = s0 > 0 ? 1 : -1;
  const side1 = s1 > 0 ? 1 : -1;
  if (side0 === side1) return 0;
  // Signed distance is linear along the path, so the line is met at t = s0 / (s0 - s1).
  const t = s0 / (s0 - s1);
  const hx = p0.x + (p1.x - p0.x) * t;
  const hy = p0.y + (p1.y - p0.y) * t;
  const abx = b.x - a.x;
  const aby = b.y - a.y;
  const length = Math.hypot(abx, aby) || 1;
  const along = ((hx - a.x) * abx + (hy - a.y) * aby) / length;
  if (along < -pad || along > length + pad) return 0;
  return side1;
}

/**
 * Axis-aligned bounds {x0, y0, x1, y1} of a collider including its radius
 * (arcs use the outer offset circle), or null for unknown types.
 */
function colliderBounds(col) {
  if (col.type === 'seg') {
    return {
      x0: Math.min(col.a.x, col.b.x) - col.r,
      y0: Math.min(col.a.y, col.b.y) - col.r,
      x1: Math.max(col.a.x, col.b.x) + col.r,
      y1: Math.max(col.a.y, col.b.y) + col.r,
    };
  }
  if (col.type === 'circle') {
    return { x0: col.c.x - col.r, y0: col.c.y - col.r, x1: col.c.x + col.r, y1: col.c.y + col.r };
  }
  if (col.type === 'arc') {
    const reach = col.R + col.r;
    return { x0: col.c.x - reach, y0: col.c.y - reach, x1: col.c.x + reach, y1: col.c.y + reach };
  }
  return null;
}

/** Geometry signature used to detect colliders that moved since the grid was built. */
function colliderSignature(col) {
  if (col.type === 'seg') return [col.a.x, col.a.y, col.b.x, col.b.y, col.r];
  if (col.type === 'circle') return [col.c.x, col.c.y, col.r];
  if (col.type === 'arc') return [col.c.x, col.c.y, col.R, col.r, col.a0, col.a1];
  return [];
}

/** True when the collider's geometry differs from the signature stored at the last grid build (no allocation). */
function signatureChanged(col) {
  const s = col._gsig;
  if (!s) return true;
  if (col.type === 'seg') {
    return s.length !== 5 || s[0] !== col.a.x || s[1] !== col.a.y || s[2] !== col.b.x || s[3] !== col.b.y ||
      s[4] !== col.r;
  }
  if (col.type === 'circle') return s.length !== 3 || s[0] !== col.c.x || s[1] !== col.c.y || s[2] !== col.r;
  if (col.type === 'arc') {
    return s.length !== 6 || s[0] !== col.c.x || s[1] !== col.c.y || s[2] !== col.R || s[3] !== col.r ||
      s[4] !== col.a0 || s[5] !== col.a1;
  }
  return s.length !== 0;
}

/** Query stamps are unique across every World so a collider shared by two worlds is never skipped. */
let nextQueryStamp = 1;

// ---------------------------------------------------------------------------
// World
// ---------------------------------------------------------------------------
class World {
  constructor() {
    // Gravity component along the 6.5 degree inclined playfield (down = -y).
    this.gravity = (5 / 7) * 9.81 * Math.sin((6.5 * Math.PI) / 180);
    this.damping = 0.2;
    // Rolling resistance coefficient: a rolling/resting contact loses
    // rollingMu x (gravity load on the contact) of tangential speed per substep.
    this.rollingMu = 0.02;
    this.colliders = [];
    this.sensors = [];
    this.flippers = [];
    this.balls = [];
    this.events = [];
    this.time = 0;
    this.maxSpeed = 6.5;
    this.nudgeImpulse = vec(0, 0);
    this.frame = 0;
    // Broadphase: uniform grid of collider bounds, built lazily by activeFor /
    // step and kept in sync with the collider list (see rebuildGrid).
    this.gridCell = 0.05;
    this._grid = null;
    this._candidates = [];
  }

  /**
   * Legacy alias: the shipped table calibration sets world.railMu; it is the
   * same knob as rollingMu (reads and writes go to rollingMu).
   */
  get railMu() {
    return this.rollingMu;
  }

  set railMu(value) {
    this.rollingMu = value;
  }

  /** Register a collider and return it. */
  add(col) {
    this.colliders.push(col);
    return col;
  }

  /** Register a sensor and return it. */
  addSensor(sensor) {
    this.sensors.push(sensor);
    return sensor;
  }

  /** Register a flipper and return it. */
  addFlipper(flipper) {
    this.flippers.push(flipper);
    return flipper;
  }

  /** Queue an event {type, t, ...data} for the rules layer. */
  emit(type, data) {
    this.events.push({ type, t: this.time, ...data });
  }

  /** First collider with the given tag. */
  byTag(tag) {
    return this.colliders.find((col) => col.tag === tag);
  }

  /** Accumulate a table nudge impulse applied over the next frame's substeps. */
  nudge(x, y) {
    this.nudgeImpulse.x += x;
    this.nudgeImpulse.y += y;
  }

  /**
   * (Re)build the broadphase grid from the current collider list: every
   * collider (active or not; activity is checked at query time so toggling
   * targets/doors needs no rebuild) is filed under each gridCell-sized cell
   * its bounds overlap. Called automatically by step() when colliders were
   * added/removed/replaced or a collider's geometry changed; call it directly
   * after editing geometry between steps if a query must see it immediately.
   */
  rebuildGrid() {
    const colliders = this.colliders;
    const cell = this.gridCell;
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    const bounds = [];
    for (const col of colliders) {
      const box = colliderBounds(col);
      bounds.push(box);
      if (!box) continue;
      if (box.x0 < minX) minX = box.x0;
      if (box.y0 < minY) minY = box.y0;
      if (box.x1 > maxX) maxX = box.x1;
      if (box.y1 > maxY) maxY = box.y1;
    }
    if (!(minX <= maxX)) {
      minX = minY = 0;
      maxX = maxY = cell;
    }
    const x0 = Math.floor(minX / cell) * cell;
    const y0 = Math.floor(minY / cell) * cell;
    const nx = Math.max(1, Math.ceil((maxX - x0) / cell) + 1);
    const ny = Math.max(1, Math.ceil((maxY - y0) / cell) + 1);
    const cells = new Array(nx * ny);
    for (let k = 0; k < cells.length; k++) cells[k] = [];
    const grid = { cell, x0, y0, nx, ny, cells, source: colliders, count: colliders.length };
    for (let index = 0; index < colliders.length; index++) {
      const col = colliders[index];
      col._gi = index;
      col._gsig = colliderSignature(col);
      const box = bounds[index];
      if (!box) continue;
      const i0 = clamp(Math.floor((box.x0 - x0) / cell), 0, nx - 1);
      const i1 = clamp(Math.floor((box.x1 - x0) / cell), 0, nx - 1);
      const j0 = clamp(Math.floor((box.y0 - y0) / cell), 0, ny - 1);
      const j1 = clamp(Math.floor((box.y1 - y0) / cell), 0, ny - 1);
      for (let j = j0; j <= j1; j++) for (let i = i0; i <= i1; i++) cells[j * nx + i].push(col);
    }
    this._grid = grid;
  }

  /** Rebuild the grid when the collider list or any collider's geometry changed since it was built. */
  _syncGrid() {
    const grid = this._grid;
    const colliders = this.colliders;
    if (!grid || grid.source !== colliders || grid.count !== colliders.length) {
      this.rebuildGrid();
      return;
    }
    for (let index = 0; index < colliders.length; index++) {
      const col = colliders[index];
      if (col._gi !== index || signatureChanged(col)) {
        this.rebuildGrid();
        return;
      }
    }
  }

  /**
   * Broadphase query: active colliders sharing a layer bit with the ball whose
   * bounds may meet the ball's swept box for the next dt seconds (position to
   * position + v * dt, padded by the ball radius plus a 1 mm skin). Candidates
   * come back in ascending collider index, so tie-breaks between equal hit
   * times are stable. The returned array is a scratch buffer reused by the
   * next query: consume it before calling activeFor again.
   */
  activeFor(ball, dt = SUBSTEP_DT) {
    if (!this._grid) this.rebuildGrid();
    const grid = this._grid;
    const pad = ball.r + 1e-3;
    const dx = ball.v.x * dt;
    const dy = ball.v.y * dt;
    const minX = Math.min(ball.p.x, ball.p.x + dx) - pad;
    const maxX = Math.max(ball.p.x, ball.p.x + dx) + pad;
    const minY = Math.min(ball.p.y, ball.p.y + dy) - pad;
    const maxY = Math.max(ball.p.y, ball.p.y + dy) + pad;
    const { cell, x0, y0, nx, ny, cells } = grid;
    const i0 = clamp(Math.floor((minX - x0) / cell), 0, nx - 1);
    const i1 = clamp(Math.floor((maxX - x0) / cell), 0, nx - 1);
    const j0 = clamp(Math.floor((minY - y0) / cell), 0, ny - 1);
    const j1 = clamp(Math.floor((maxY - y0) / cell), 0, ny - 1);
    const stamp = nextQueryStamp++;
    const out = this._candidates;
    out.length = 0;
    for (let j = j0; j <= j1; j++) {
      for (let i = i0; i <= i1; i++) {
        for (const col of cells[j * nx + i]) {
          if (col._qs === stamp) continue;
          col._qs = stamp;
          if (col.active && col.layers & ball.layer) out.push(col);
        }
      }
    }
    if (out.length > 1) out.sort((a, b) => a._gi - b._gi);
    return out;
  }

  /**
   * Advance one 120 Hz frame in SUBSTEPS substeps: flippers first, then each
   * live ball is integrated, swept against statics, collided with the other
   * balls, pushed by flippers and speed clamped. Sensors are evaluated once per
   * frame against prevOuter (the frame-start position) so fast balls cannot
   * skip a line sensor.
   */
  step() {
    const dt = SUBSTEP_DT;
    this._syncGrid();
    for (const ball of this.balls) ball.prevOuter = { x: ball.p.x, y: ball.p.y };
    for (let substep = 0; substep < SUBSTEPS; substep++) {
      for (const flipper of this.flippers) flipper.substep(dt);
      for (const ball of this.balls) {
        if (!ball.active || ball.held) {
          ball.prevP = { ...ball.p };
          continue;
        }
        this.integrate(ball, dt);
        this.collideStatics(ball, dt);
        this.collideBalls(ball);
        this.collideFlippers(ball);
        this.speedLimit(ball);
      }
      this.time += dt;
    }
    for (const ball of this.balls) if (ball.active && !ball.held) this.checkSensors(ball);
    this.nudgeImpulse = vec(0, 0);
    this.frame++;
  }

  /** Apply gravity, a quarter of the frame's nudge impulse, and exponential air damping. */
  integrate(ball, dt) {
    ball.prevP = { ...ball.p };
    // Project gravity onto the sloped ramp surface. Raised height is potential
    // energy: climbing slows the ball, descending accelerates it, without a timer kick.
    const slope = ball.rampId && ball.rampSlope;
    if (slope) {
      const normalGravity = (5 / 7) * 9.81 * Math.cos(6.5 * Math.PI / 180);
      const correction = (normalGravity - this.gravity * slope.y) / (1 + slope.x ** 2 + slope.y ** 2);
      ball.v.x -= slope.x * correction * dt;
      ball.v.y -= (this.gravity + slope.y * correction) * dt;
    } else ball.v.y -= this.gravity * dt;
    ball.v.x += this.nudgeImpulse.x / SUBSTEPS;
    ball.v.y += this.nudgeImpulse.y / SUBSTEPS;
    const decay = Math.exp(-this.damping * dt);
    ball.v.x *= decay;
    ball.v.y *= decay;
    ball.spin += len(ball.v) * dt;
  }

  /**
   * Continuous collision against static colliders: up to 6 sweep iterations
   * consume the substep time, each moving to the earliest hit (backed off by
   * 1e-6 s) and resolving it. A hit whose normal is nearly parallel (dot >
   * 0.93) to the previous one is rejected so the ball cannot get pinned on the
   * same face. A final discrete push-out pass fixes any residual overlap.
   * One-way segments are skipped entirely (sweep and push-out) while the ball
   * moves along their pass direction, so passing a gate never jumps.
   */
  collideStatics(ball, dt) {
    let remaining = dt;
    let lastNormal = null;
    for (let iter = 0; iter < 6 && remaining > EPS; iter++) {
      const v = ball.v;
      const active = this.activeFor(ball, remaining);
      let best = null;
      let bestCol = null;
      for (const col of active) {
        let hit = null;
        if (col.type === 'seg') {
          if (col.oneWay && dot(v, col.oneWay) > 0) continue;
          hit = sweepSegment(ball.p, v, remaining, col, col.r + ball.r);
        } else if (col.type === 'circle') {
          hit = sweepCircle(ball.p, v, remaining, col.c, col.r + ball.r);
        } else if (col.type === 'arc') {
          hit = sweepArc(ball.p, v, remaining, col, ball.r);
        }
        if (hit && lastNormal && hit.n.x * lastNormal.x + hit.n.y * lastNormal.y > 0.93) hit = null;
        if (hit && (!best || hit.t < best.t)) {
          best = hit;
          bestCol = col;
        }
      }
      if (!best) {
        ball.p = add(ball.p, scale(v, remaining));
        break;
      }
      ball.p = add(ball.p, scale(v, Math.max(0, best.t - 1e-6)));
      remaining -= best.t;
      this.resolve(ball, best.n, bestCol);
      lastNormal = best.n;
    }
    for (const col of this.activeFor(ball, 0)) {
      if (col.oneWay && dot(ball.v, col.oneWay) > 0) continue;
      const n = pushOut(ball, col);
      if (n && dot(ball.v, n) < 0) this.resolve(ball, n, col, true);
    }
  }

  /**
   * Impulse response against normal n. Restitution ramps with the closing
   * speed (restitutionScale: none below 0.15 m/s, full from 0.6 m/s) and is
   * always 0 for discrete push-outs. Friction has two regimes:
   *  - impact (|vn| >= REST_SPEED, from a sweep): Coulomb friction with the
   *    collider's mu, min(mu * (1 + e) * |vn|, |vt|);
   *  - resting/rolling (|vn| < REST_SPEED or a push-out): rolling resistance
   *    min(rollingMu * gravity * SUBSTEP_DT * |n.y| * grip, |vt|), i.e.
   *    proportional to the gravity load the contact carries this substep,
   *    with grip = min(1, |vt| / CREEP_SPEED) fading it out at rest. A ball
   *    rolls freely on any slope steeper than atan(rollingMu), creeps down
   *    shallower ones at a few mm/s and never sticks to a face or balances on
   *    a cap.
   * Events: 'contact' (walls) and 'hit:<tag>' for impacts above 0.25 m/s, or a
   * quieter 'hit:<tag>' above 0.02 m/s; each (collider, ball) pair is
   * throttled to one event per 0.06 s via ball._lastEv, so simultaneous hits
   * by different balls all report.
   */
  resolve(ball, n, col, discrete = false) {
    const vn = dot(ball.v, n);
    if (vn >= 0) return;
    const tangent = perp(n);
    const vt = dot(ball.v, tangent);
    const speed = -vn;
    const e = discrete ? 0 : (col.e || 0) * restitutionScale(speed);
    let frictionImpulse;
    if (!discrete && speed >= REST_SPEED) {
      frictionImpulse = Math.min((col.mu || 0) * (1 + e) * speed, Math.abs(vt));
    } else {
      const load = this.gravity * SUBSTEP_DT * Math.abs(n.y);
      const grip = Math.min(1, Math.abs(vt) / CREEP_SPEED);
      frictionImpulse = Math.min(this.rollingMu * load * grip, Math.abs(vt));
    }
    const newVn = speed * e;
    const newVt = vt - Math.sign(vt) * frictionImpulse;
    ball.v = add(scale(n, newVn), scale(tangent, newVt));
    if (speed <= 0.02) return;
    const now = this.time;
    if (!ball._lastEv) ball._lastEv = new Map();
    const last = ball._lastEv.get(col);
    if (last !== undefined && now - last <= 0.06) return;
    if (speed > 0.25) {
      ball._lastEv.set(col, now);
      if (col.kind === 'wall') {
        this.emit('contact', { tag: col.tag, id: col.id, speed, p: { ...ball.p }, ball: ball.id });
      }
      if (col.tag) this.emit('hit:' + col.tag, { speed, p: { ...ball.p }, ball: ball.id, colId: col.id });
    } else if (col.tag) {
      ball._lastEv.set(col, now);
      this.emit('hit:' + col.tag, { speed, p: { ...ball.p }, ball: ball.id, colId: col.id });
    }
  }

  /**
   * Ball-ball collisions for one ball against every other live ball on a
   * shared layer (equal masses). Overlapping pairs are separated
   * symmetrically; approaching pairs exchange normal velocity with e = 0.9
   * (through the same restitution ramp as walls, so stacked resting balls stay
   * still). 'ballHit' {a, b, speed, p} fires for closing speeds above 0.3 m/s.
   */
  collideBalls(ball) {
    for (const other of this.balls) {
      if (other === ball || !other.active || other.held || !(other.layer & ball.layer)) continue;
      const rel = sub(ball.p, other.p);
      const dist = len(rel);
      const minDist = ball.r + other.r;
      if (dist >= minDist) continue;
      const n = dist > EPS ? scale(rel, 1 / dist) : vec(0, 1);
      if (dist < minDist - 1e-6) {
        const push = (minDist - dist) * 0.5;
        ball.p = add(ball.p, scale(n, push));
        other.p = sub(other.p, scale(n, push));
      }
      const vn = dot(sub(ball.v, other.v), n);
      if (vn >= 0) continue;
      const speed = -vn;
      const e = 0.9 * restitutionScale(speed);
      const impulse = ((1 + e) * speed) / 2;
      ball.v = add(ball.v, scale(n, impulse));
      other.v = sub(other.v, scale(n, impulse));
      if (speed > 0.3) {
        const p = vec((ball.p.x + other.p.x) / 2, (ball.p.y + other.p.y) / 2);
        this.emit('ballHit', { a: ball.id, b: other.id, speed, p });
      }
    }
  }

  /**
   * Flipper contact. The ball remembers which side of each flipper it was on
   * (_side<side>) while clear of it; if it then appears on the other side
   * while touching the shaft it is treated as having tunnelled and the normal
   * is forced back toward the remembered side. The bat surface velocity is
   * 0.85 x the rim speed from the real substep angVel (0 while the bat sits on
   * a stop, so a resting bat never strikes). Once per frame per flipper
   * (_flipVel<side>) an approaching ball gets the full impulse with
   * restitution 0.06 (0 when slow) and friction 0.25; on the other substeps of
   * the frame it is only kept from sinking into the moving face. 'flipperHit'
   * fires for closing speeds above 0.3 m/s.
   */
  collideFlippers(ball) {
    if (ball.layer !== 1) return;
    for (const flipper of this.flippers) {
      const tip = flipper.tip();
      const { q, t } = closestPointOnSegment(ball.p, flipper.pivot, tip);
      const minDist = flipper.radiusAt(t) + ball.r;
      const rel = sub(ball.p, q);
      const dist = len(rel);
      const axis = sub(tip, flipper.pivot);
      const side = axis.x * (ball.p.y - flipper.pivot.y) - axis.y * (ball.p.x - flipper.pivot.x) >= 0 ? 1 : -1;
      const sideKey = '_side' + flipper.side;
      if (dist >= minDist + 6e-4) {
        ball[sideKey] = side;
        continue;
      }
      let n = dist > EPS ? scale(rel, 1 / dist) : vec(0, 1);
      if (ball[sideKey] && side !== ball[sideKey] && t > 0.001 && t < 0.999) {
        // Ball crossed the shaft between substeps: push it back to its previous side.
        const axisDir = norm(axis);
        n = scale(perp(axisDir), ball[sideKey]);
      }
      if (dist < minDist) ball.p = add(q, scale(n, minDist + 1e-5));
      const arm = sub(q, flipper.pivot);
      const faceVel = scale(perp(arm), flipper.angVel * 0.85);
      const relV = sub(ball.v, faceVel);
      const relN = dot(relV, n);
      if (relN >= 0) continue;
      const velKey = '_flipVel' + flipper.side;
      if (ball[velKey] === this.frame) {
        // Already struck this frame: non-penetration only.
        ball.v = add(ball.v, scale(n, -relN));
        continue;
      }
      ball[velKey] = this.frame;
      const tangent = perp(n);
      const relT = dot(relV, tangent);
      const e = Math.abs(relN) < 1 ? 0 : 0.06;
      const mu = 0.25;
      const normalImpulse = (1 + e) * Math.abs(relN);
      const frictionImpulse = Math.min(mu * normalImpulse, Math.abs(relT));
      const newRelN = -relN * e;
      const newRelT = relT - Math.sign(relT) * frictionImpulse;
      ball.v = add(faceVel, add(scale(n, newRelN), scale(tangent, newRelT)));
      if (Math.abs(relN) > 0.3) {
        this.emit('flipperHit', { side: flipper.side, speed: Math.abs(relN), p: { ...ball.p }, ball: ball.id });
      }
    }
  }

  /** Safety cap: rescale velocity to maxSpeed. (Pure clamp; no low-speed nudging.) */
  speedLimit(ball) {
    const speed = len(ball.v);
    if (speed > this.maxSpeed) ball.v = scale(ball.v, this.maxSpeed / speed);
  }

  /**
   * Sensor evaluation (once per frame). Circles emit enter/exit when the ball
   * centre crosses the radius. Lines emit 'cross:<tag>' with dir = the side
   * the ball ends on when the frame path (prevOuter -> p) crosses the sensor
   * segment extended by the ball radius at both ends (crossesSegment); there
   * is no bounding-box slop, so neighbouring lanes never trip each other.
   */
  checkSensors(ball) {
    for (const sensor of this.sensors) {
      if (!sensor.active || !(sensor.layers & ball.layer)) continue;
      if (sensor.type === 'scircle') {
        const inside = len(sub(ball.p, sensor.c)) < sensor.r;
        const wasInside = sensor.inside.has(ball.id);
        if (inside && !wasInside) {
          sensor.inside.add(ball.id);
          this.emit('enter:' + sensor.tag, { ball: ball.id, p: { ...ball.p }, v: { ...ball.v } });
        } else if (!inside && wasInside) {
          sensor.inside.delete(ball.id);
          this.emit('exit:' + sensor.tag, { ball: ball.id, p: { ...ball.p }, v: { ...ball.v } });
        }
      } else if (sensor.type === 'sline') {
        const from = ball.prevOuter || ball.prevP;
        const dir = crossesSegment(from, ball.p, sensor.a, sensor.b, sensor.pad ?? ball.r);
        if (dir !== 0) {
          this.emit('cross:' + sensor.tag, { ball: ball.id, dir, speed: len(ball.v), p: { ...ball.p } });
        }
      }
    }
  }

  /** Return and clear the queued events. */
  drainEvents() {
    const events = this.events;
    this.events = [];
    return events;
  }
}
